// @figma file=tbd node=tbd name="InputMask" set="tbd" verified=tbd
import * as React from 'react';
import { IMaskInput } from 'react-imask';
import { cn } from '../../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export type InputMaskProps = {
  /** The mask pattern string (e.g. `"000-00-0000"` for SSN, `"(000) 000-0000"` for phone).
   *  Supports react-imask mask syntax: `0` = digit, `a` = letter, `*` = alphanumeric. */
  mask: string;
  /** Controlled value. */
  value?: string;
  /** Change handler — receives the unmasked (raw) value. */
  onValueChange?: (value: string) => void;
  /** Change handler — receives the masked (formatted) value. */
  onMaskedChange?: (masked: string) => void;
  /** Additional CSS classes. */
  className?: string;
  /** Whether to expose the raw (unmasked) value via `onValueChange`. Defaults to `true`. */
  unmask?: boolean;
  /** Placeholder character shown for unfilled positions. Defaults to `_`. */
  placeholderChar?: string;
  /** Lazy mode — hides the mask until the user starts typing. Defaults to `true`. */
  lazy?: boolean;
  /** Forwarded to the underlying `<input>` element. */
  id?: string;
  name?: string;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  required?: boolean;
  autoComplete?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false' | 'grammar' | 'spelling';
};

// ─── Component ────────────────────────────────────────────────────────────────

const InputMask = React.forwardRef<HTMLInputElement, InputMaskProps>(
  (
    {
      mask,
      value,
      onValueChange,
      onMaskedChange,
      className,
      unmask = true,
      placeholderChar = '_',
      lazy = true,
      ...props
    },
    ref,
  ) => {
    const inputCls = cn(
      'flex h-9 w-full',
      'border border-input bg-input-bg px-3 py-2 text-base text-foreground shadow-sm sm:text-sm',
      'rounded-[var(--field-radius,var(--radius))]',
      'placeholder:text-muted-foreground',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
      'disabled:cursor-not-allowed disabled:opacity-50',
      'read-only:bg-muted read-only:cursor-default',
      className,
    );

    return (
      <IMaskInput
        mask={mask}
        value={value}
        unmask={unmask}
        placeholderChar={placeholderChar}
        lazy={lazy}
        inputRef={ref}
        className={inputCls}
        onAccept={(val, maskRef) => {
          if (unmask) {
            onValueChange?.(val as string);
          } else {
            onValueChange?.(maskRef.value);
          }
          onMaskedChange?.(maskRef.value);
        }}
        {...props}
      />
    );
  },
);
InputMask.displayName = 'InputMask';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { InputMask };
